import axios from '@/api/provider'

/**
 * Submit general site feedback (not tied to a specific entity).
 * Backend endpoint: POST /site-feedback (to be implemented).
 *
 * @param {Object} payload
 * @param {string} payload.category - 'bug' | 'idea' | 'praise' | 'other'
 * @param {string} payload.message - Free-text feedback
 * @param {string} payload.route - Route path the user was on (e.g. '/companies/foo')
 * @param {string} payload.userAgent - navigator.userAgent
 * @param {{width:number, height:number}} payload.viewport
 * @returns {Promise<Object>}
 */
const submit = async ({ category, message, route, userAgent, viewport }) => {
  return axios
    .post('site-feedback', {
      category,
      message,
      route,
      user_agent: userAgent,
      viewport_width: viewport?.width ?? null,
      viewport_height: viewport?.height ?? null,
    })
    .then((response) => ({
      success: response.success !== false,
      feedback: response.feedback || response,
    }))
    .catch((error) => {
      console.error('Error submitting site feedback:', error)
      throw error
    })
}

/**
 * Admin: list site-feedback submissions with optional filters.
 * @param {Object} filters
 * @param {string|null} filters.status - 'new' | 'triaged' | 'resolved' | 'wontfix'
 * @param {string|null} filters.category - 'bug' | 'idea' | 'praise' | 'other'
 * @param {number|null} filters.userId
 * @param {Date|string|null} filters.since
 * @param {number} filters.limit
 * @param {number} filters.offset
 * @returns {Promise<{ items: Array, total: number }>}
 */
const list = async ({ status, category, userId, since, limit = 50, offset = 0 } = {}) => {
  const params = {}
  if (status) params.status = status
  if (category) params.category = category
  if (userId) params.user_id = userId
  if (since) params.since = since instanceof Date ? since.toISOString() : since
  params.limit = limit
  params.offset = offset
  return axios
    .get('site-feedback', { params })
    .then((response) => ({
      items: response.items || [],
      total: response.total ?? (response.items?.length ?? 0),
    }))
    .catch((error) => {
      console.error('Error listing site feedback:', error)
      throw error
    })
}

const get = async (id) => {
  return axios
    .get(`site-feedback/${id}`)
    .catch((error) => {
      console.error('Error fetching site feedback:', error)
      throw error
    })
}

/**
 * Admin: triage an item — update status and/or notes.
 * @param {number} id
 * @param {Object} updates - { status?, notes? }
 */
const triage = async (id, updates) => {
  return axios
    .patch(`site-feedback/${id}`, updates)
    .catch((error) => {
      console.error('Error updating site feedback:', error)
      throw error
    })
}

const remove = async (id) => {
  return axios
    .delete(`site-feedback/${id}`)
    .then((response) => ({ success: response.success !== false }))
    .catch((error) => {
      console.error('Error deleting site feedback:', error)
      throw error
    })
}

const exportCsv = async (filters = {}) => {
  const instance = axios.getAxiosInstance()
  const config = axios.getConfig()
  const params = {}
  if (filters.status) params.status = filters.status
  if (filters.category) params.category = filters.category
  if (filters.userId) params.user_id = filters.userId
  if (filters.since) params.since = filters.since instanceof Date ? filters.since.toISOString() : filters.since
  const response = await instance.get('site-feedback/export', {
    ...config,
    params,
    responseType: 'blob',
  })
  return response.data
}

export const siteFeedbackService = {
  submit,
  list,
  get,
  triage,
  remove,
  exportCsv,
}
